import { ERROR_CODES, type ErrorCode, ErrorEnvelopeSchema } from '@vision360/contracts';

/**
 * Errors surfaced by the API client. They carry the API's error *code*; the UI
 * maps codes to French i18n keys and never displays the server's message,
 * which is English by design (TechSpec §8.4).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'UNKNOWN',
    readonly requestId?: string,
    /** The envelope's details, e.g. VALIDATION issues { path, message }[]. */
    readonly details?: unknown,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'ApiError';
  }
}

/** The request never produced a response: offline, DNS, server down. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('network request failed', { cause });
    this.name = 'NetworkError';
  }
}

const KNOWN_CODES = new Set<string>(ERROR_CODES);

export async function toApiError(response: Response): Promise<ApiError> {
  try {
    const parsed = ErrorEnvelopeSchema.safeParse(await response.json());
    if (parsed.success) {
      const { code, requestId, details } = parsed.data.error;
      return new ApiError(
        response.status,
        KNOWN_CODES.has(code) ? code : 'UNKNOWN',
        requestId,
        details,
      );
    }
  } catch {
    // Not JSON — fall through.
  }
  return new ApiError(response.status, 'UNKNOWN');
}
